import { Button, cn, Label } from '@crm/ui';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';

interface PhotoFieldProps {
  label: string;
  /** Endereco publico ou a propria imagem embutida (data URL). */
  value: string;
  onChange: (value: string) => void;
  /** O que aparece no lugar da foto: iniciais ou um icone. */
  fallback?: React.ReactNode;
  /** Redonda para gente, quadrada para servico, categoria e logo. */
  shape?: 'circle' | 'square';
  hint?: string;
  className?: string;
}

/** Lado maior da imagem gravada - o suficiente para avatar e miniatura. */
const MAX_LADO = 512;

/**
 * Reduz a imagem no proprio navegador e devolve como data URL. Sem isso, a
 * foto de um celular moderno chega com varios megabytes e nao cabe no campo.
 */
export function reduzirImagem(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();

    leitor.onerror = () => reject(new Error('Nao foi possivel ler o arquivo'));
    leitor.onload = () => {
      const img = new Image();

      img.onerror = () => reject(new Error('Arquivo nao e uma imagem'));
      img.onload = () => {
        const escala = Math.min(1, MAX_LADO / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * escala);
        canvas.height = Math.round(img.height * escala);

        const contexto = canvas.getContext('2d');
        if (!contexto) {
          reject(new Error('Nao foi possivel processar a imagem'));
          return;
        }

        contexto.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };

      img.src = String(leitor.result);
    };

    leitor.readAsDataURL(file);
  });
}

/**
 * Foto de um cadastro: mostra a atual, troca por um arquivo do aparelho e
 * remove. O endereco fica escondido - quem cadastra escolhe a imagem, nao
 * digita uma URL.
 */
export function PhotoField({
  label,
  value,
  onChange,
  fallback,
  shape = 'circle',
  hint,
  className,
}: PhotoFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  const moldura = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  async function escolher(file: File | undefined) {
    if (!file) return;

    setErro(null);

    try {
      onChange(await reduzirImagem(file));
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Nao foi possivel usar esta imagem');
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={inputId}>{label}</Label>

      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className={cn('h-16 w-16 shrink-0 border border-border object-cover', moldura)}
          />
        ) : (
          <span
            className={cn(
              'flex h-16 w-16 shrink-0 items-center justify-center border border-dashed border-border bg-muted text-muted-foreground',
              moldura,
            )}
          >
            {fallback ?? <ImagePlus className="h-6 w-6" />}
          </span>
        )}

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            <ImagePlus className="h-4 w-4" />
            {value ? 'Trocar foto' : 'Escolher foto'}
          </Button>

          {value ? (
            <Button type="button" variant="ghost" onClick={() => onChange('')}>
              <Trash2 className="h-4 w-4" />
              Remover
            </Button>
          ) : null}
        </div>
      </div>

      {/* O input fica fora do fluxo: quem clica ve os botoes acima. */}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => {
          void escolher(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      {erro ? <p className="text-sm text-destructive">{erro}</p> : null}
      {!erro && hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
